import { useEffect, useRef, useState } from "react";
import { productImageError } from "@admin/_helpers/productImageFile";

// A file chosen for the new product, with the object URL its preview shows.
export interface PickedImage {
    file: File;
    previewUrl: string;
}

// The same file, as far as a browser can tell: it gives a page no path, so the
// name, size and last-modified time are all there is to compare.
const sameFileKey = (file: File) =>
    `${file.name}\u0000${file.size}\u0000${file.lastModified}`;

// The Add Product page's chosen images, held until the create sends them.
// Each preview is an object URL, released when its image is removed, when the
// list is cleared, and when the page unmounts with images still chosen.
export const usePickedImages = () => {
    const [images, setImages] = useState<PickedImage[]>([]);
    // One message per file the last add refused; the next add replaces them.
    const [refusals, setRefusals] = useState<string[]>([]);
    const liveUrls = useRef(new Set<string>());

    useEffect(() => {
        const urls = liveUrls.current;

        return () => {
            urls.forEach((url) => URL.revokeObjectURL(url));
            urls.clear();
        };
    }, []);

    const add = (files: File[]) => {
        const accepted: PickedImage[] = [];
        const refused: string[] = [];
        // The files already chosen, and each one this add accepts.
        const chosen = new Set(images.map((image) => sameFileKey(image.file)));

        files.forEach((file) => {
            const error = productImageError(file);

            if (error) {
                refused.push(error);
                return;
            }

            if (chosen.has(sameFileKey(file))) {
                refused.push(`${file.name} was not added: it is already chosen.`);
                return;
            }

            chosen.add(sameFileKey(file));
            const previewUrl = URL.createObjectURL(file);
            liveUrls.current.add(previewUrl);
            accepted.push({ file, previewUrl });
        });

        setImages((current) => [...current, ...accepted]);
        setRefusals(refused);
    };

    const remove = (previewUrl: string) => {
        URL.revokeObjectURL(previewUrl);
        liveUrls.current.delete(previewUrl);
        setImages((current) =>
            current.filter((image) => image.previewUrl !== previewUrl),
        );
    };

    const clear = () => {
        liveUrls.current.forEach((url) => URL.revokeObjectURL(url));
        liveUrls.current.clear();
        setImages([]);
        setRefusals([]);
    };

    return { images, refusals, add, remove, clear };
};
